import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { CircularProgress, Box, Typography, Paper, Button } from "@mui/material";
import AuthenticationProvider, {
  useAuthentication,
} from "@iad-os/react-ghost-auth";
import type { AppConfiguration } from "@kappa-v2/contracts";
import { loadAppConfiguration } from "@kappa-v2/sdk";
import { useConfigStore } from "./store/configStore.js";
import { theme } from "./theme.js";
import { Toaster } from "./components/Toaster.js";
import { VersionWatcher } from "./components/VersionWatcher.js";
import { InstallPrompt } from "./components/InstallPrompt.js";
import { GlobalLoader } from "./components/GlobalLoader.js";
import { Layout } from "./components/Layout.js";
import { HomePage } from "./pages/Home.js";
import { UserPage } from "./pages/UserPage.js";
import { PartnersPage } from "./pages/PartnersPage.js";
import { SuppliersPage } from "./pages/SuppliersPage.js";
import { ServicesPage } from "./pages/ServicesPage.js";
import { ServicesAcquiredPage } from "./pages/ServicesAcquiredPage.js";
import { PartnerServicesPage } from "./pages/PartnerServicesPage.js";
import { CreditsPage } from "./pages/CreditsPage.js";
import { CreditsGenerationPage } from "./pages/CreditsGenerationPage.js";
import { DebitsPage } from "./pages/DebitsPage.js";
import { ReceiptsPage } from "./pages/ReceiptsPage.js";
import { ReceiptDetailPage } from "./pages/ReceiptDetailPage.js";
import { CostsPage } from "./pages/CostsPage.js";
import { SummaryPage } from "./pages/SummaryPage.js";
import { StatisticsPage } from "./pages/StatisticsPage.js";
import { WhatsAppComposePage } from "./pages/whatsapp/ComposePage.js";
import { WhatsAppQueuesPage } from "./pages/whatsapp/QueuesPage.js";
import { WhatsAppTemplatesPage } from "./pages/whatsapp/TemplatesPage.js";
import { WhatsAppMessagesPage } from "./pages/whatsapp/MessagesPage.js";
import "./i18n/index.js";
import "./styles.css";

// ─── Discovery URL ────────────────────────────────────────────────────────────

const DISCOVERY_URL =
  // NB: usare `||` e non `??` — il Dockerfile può iniettare VITE_DISCOVERY_URL=""
  // (build-arg non passato) e la stringa vuota NON deve sovrascrivere il default.
  import.meta.env["VITE_DISCOVERY_URL"] ||
  "/.well-known/app-configuration";

// ─── Protected area ───────────────────────────────────────────────────────────

// Decode a JWT payload without throwing — returns null if invalid
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

// Inner component: only rendered when authenticated
function AuthenticatedArea() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { logout, token } = useAuthentication() as any;

  const userInfo = token?.id_token ? decodeJwtPayload(token.id_token) as {
    sub?: string;
    preferred_username?: string;
    email?: string;
    name?: string;
    realm_access?: { roles: string[] };
  } | null : null;

  const authClaims = userInfo
    ? {
        sub: userInfo.sub ?? "",
        preferred_username: userInfo.preferred_username,
        email: userInfo.email,
        name: userInfo.name,
        realm_access: userInfo.realm_access
      }
    : null;

  return (
    <Layout
      onLogout={logout}
      userName={userInfo?.name ?? userInfo?.preferred_username}
      userEmail={userInfo?.email}
    >
      <Routes>
        <Route index element={<HomePage />} />
        <Route path="partners" element={<PartnersPage />} />
        <Route path="suppliers" element={<SuppliersPage />} />
        <Route path="services" element={<ServicesPage />} />
        <Route path="partner-services" element={<PartnerServicesPage />} />
        <Route path="partnersServices" element={<PartnerServicesPage />} />
        <Route path="services-acquired" element={<ServicesAcquiredPage />} />
        <Route path="credits" element={<CreditsPage />} />
        <Route path="credits-generate" element={<CreditsGenerationPage />} />
        <Route path="debits" element={<DebitsPage />} />
        <Route path="receipts" element={<ReceiptsPage />} />
        <Route path="receipt/:id/partner/:partnerId/period/:period" element={<ReceiptDetailPage />} />
        <Route path="costs" element={<CostsPage />} />
        <Route path="summary" element={<SummaryPage />} />
        <Route path="statistics" element={<StatisticsPage />} />
        <Route path="whatsapp/compose" element={<WhatsAppComposePage />} />
        <Route path="whatsapp/queues" element={<WhatsAppQueuesPage />} />
        <Route path="whatsapp/templates" element={<WhatsAppTemplatesPage />} />
        <Route path="whatsapp/messages" element={<WhatsAppMessagesPage />} />
        <Route path="users" element={<UserPage user={authClaims} />} />
        <Route path="*" element={<Navigate to="" replace />} />
      </Routes>
      <InstallPrompt />
    </Layout>
  );
}

function ProtectedArea() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { isAuthenticated, login, status } = useAuthentication() as any;

  // Auto-trigger login as soon as the library settles on LOGGED-OUT
  useEffect(() => {
    if (status === "LOGGED-OUT") {
      login();
    }
  }, [status, login]);

  // While OIDC code exchange or initial check is in progress, show a spinner
  if (status === "INIT" || status === "LOGGING" || status === "LOGGED-OUT") {
    return (
      <Box sx={{
        display: "flex", justifyContent: "center", alignItems: "center",
        minHeight: "100vh", flexDirection: "column", gap: 2,
        background: "linear-gradient(135deg, #0F1F14 0%, #1B3A24 100%)"
      }}>
        <CircularProgress sx={{ color: "#52B788" }} />
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
          {status === "LOGGING" ? "Accesso in corso..." : "Verifica sessione..."}
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated()) {
    // Fallback: trigger login if somehow we end up here unauthenticated
    login();
    return null;
  }

  // Only render AuthenticatedArea (which calls useUserInfo) once we are sure
  // the user is authenticated — useUserInfo throws if called without a token.
  return <AuthenticatedArea />;
}

// ─── Public area ─────────────────────────────────────────────────────────────

function PublicArea() {
  const { login } = useAuthentication();
  return (
    <Box sx={{
      minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center", p: 2,
      background:
        "radial-gradient(1200px 600px at 15% -10%, #2e7d32 0%, rgba(46,125,50,0) 60%)," +
        "radial-gradient(1000px 700px at 100% 110%, #1b5e20 0%, rgba(27,94,32,0) 55%)," +
        "linear-gradient(135deg, #0f1f14 0%, #173a22 55%, #1b5e20 100%)"
    }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%", maxWidth: 420, borderRadius: 4, p: { xs: 3, sm: 5 }, textAlign: "center",
          bgcolor: "rgba(255,255,255,0.97)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.45)",
          border: "1px solid rgba(255,255,255,0.5)"
        }}
      >
        <Box
          component="img"
          src="/logo-kappa.png"
          alt="Kappa Equestre"
          sx={{ height: 84, mb: 2, objectFit: "contain", filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.15))" }}
        />
        <Typography variant="h5" sx={{ fontWeight: 800, color: "#1B5E20", letterSpacing: "-0.3px" }}>
          Kappa Equestre
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, mb: 3.5 }}>
          Gestione del maneggio · area riservata
        </Typography>
        <Button
          fullWidth
          size="large"
          variant="contained"
          onClick={() => login()}
          sx={{
            borderRadius: 2, py: 1.3, fontWeight: 700, fontSize: "1rem", textTransform: "none",
            background: "linear-gradient(135deg, #1B5E20, #2E7D32)",
            boxShadow: "0 8px 18px rgba(27,94,32,0.35)",
            "&:hover": { filter: "brightness(1.05)", transform: "translateY(-1px)", boxShadow: "0 12px 24px rgba(27,94,32,0.45)" }
          }}
        >
          Accedi
        </Button>
        <Typography variant="caption" sx={{ display: "block", color: "text.disabled", mt: 2.5 }}>
          Accesso sicuro · Keycloak SSO
        </Typography>
      </Paper>
    </Box>
  );
}

// ─── Authenticated routes (needs BrowserRouter context for useNavigate) ───────

interface AppRoutesProps {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint: string;
  client_id: string;
}

function AppRoutes({ issuer, authorization_endpoint, token_endpoint, end_session_endpoint, client_id }: AppRoutesProps) {
  const navigate = useNavigate();

  return (
    <AuthenticationProvider
      config={{
        providers: [
          {
            name: "keycloak",
            issuer,
            authorization_endpoint,
            token_endpoint,
            end_session_endpoint,
            client_id,
            requested_scopes: "openid profile email",
            redirect_uri: window.location.origin + "/protected",
            redirect_logout_uri: window.location.origin + "/public",
            pkce: true
          }
        ]
      }}
      onRoute={(path: string) => {
        // The library passes a full URL (e.g. https://host/protected) — extract path only
        try {
          const { pathname, search, hash } = new URL(path);
          navigate(pathname + search + hash, { replace: true });
        } catch {
          navigate(path, { replace: true });
        }
      }}
    >
      <Routes>
        <Route path="/public/*" element={<PublicArea />} />
        <Route path="/protected/*" element={<ProtectedArea />} />
        <Route path="*" element={<Navigate to="/protected" replace />} />
      </Routes>
    </AuthenticationProvider>
  );
}

// ─── App bootstrap ────────────────────────────────────────────────────────────

export function App() {
  const [discoveryConfig, setDiscoveryConfig] = useState<AppConfiguration | null>(null);
  const [discoveryError, setDiscoveryError] = useState<string | null>(null);
  const { setConfig } = useConfigStore();

  useEffect(() => {
    loadAppConfiguration(DISCOVERY_URL)
      .then((cfg: AppConfiguration) => {
        setDiscoveryConfig(cfg);
        setConfig(cfg);
      })
      .catch((err: unknown) => {
        setDiscoveryError(err instanceof Error ? err.message : "Errore discovery");
      });
  }, [setConfig]);

  if (discoveryError) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{
          display: "flex", justifyContent: "center", alignItems: "center",
          minHeight: "100vh", flexDirection: "column", gap: 2,
          bgcolor: "background.default"
        }}>
          <Typography variant="h6" color="error" fontWeight={700}>
            Impossibile caricare la configurazione
          </Typography>
          <Typography variant="body2" color="text.secondary">{discoveryError}</Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontFamily: "monospace" }}>
            {DISCOVERY_URL}
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  if (!discoveryConfig) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{
          display: "flex", justifyContent: "center", alignItems: "center",
          minHeight: "100vh", gap: 2, flexDirection: "column",
          background: "linear-gradient(135deg, #0F1F14 0%, #1B3A24 100%)"
        }}>
          <CircularProgress sx={{ color: "#52B788" }} />
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
            Caricamento configurazione...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  const oidcProvider = discoveryConfig.metadata.providers[0];
  if (!oidcProvider) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ p: 4 }}>
          <Typography color="error">Nessun provider OIDC configurato</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalLoader />
      <Toaster />
      <VersionWatcher />
      <BrowserRouter>
        <AppRoutes
          issuer={oidcProvider.issuer}
          authorization_endpoint={oidcProvider.authorization_endpoint}
          token_endpoint={oidcProvider.token_endpoint}
          end_session_endpoint={oidcProvider.end_session_endpoint}
          client_id={oidcProvider.client_id}
        />
      </BrowserRouter>
    </ThemeProvider>
  );
}
